import api from '@/services/api/client';
import {requireRFC3339, requireUUID} from '@/services/api/contractValidation';
import {getV2Error} from '@/services/api/v2';
import {WyscoutAnalyticsLens, wyscoutAnalyticsLenses} from './wyscoutAnalyticsApi';

export interface PlayerAIProfileScope {
  lens: WyscoutAnalyticsLens;
  seasonId: number | null;
  matchIds: number[];
  peerIds: string[];
}

export interface PlayerAIProfileSample {
  playerId: string;
  name: string;
  position: string;
  appearances: number;
  starts: number | null;
  minutes: number;
  requestedGames: number;
  limited: boolean;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface PlayerAIProfileClaim {
  title: string;
  analysis: string;
  evidenceRefs: string[];
}

export interface PlayerAIProfileReport {
  archetype: {title: string; summary: string};
  evidenceSummary: string;
  strengths: PlayerAIProfileClaim[];
  developmentPriorities: PlayerAIProfileClaim[];
  tacticalFit: Array<{role: string; analysis: string; evidenceRefs: string[]}>;
  peerComparisons: Array<{peerId: string; analysis: string; evidenceRefs: string[]}>;
  omittedPeers: Array<{peerId: string; name: string; reason: string}>;
  confidence: {level: 'low' | 'medium' | 'high'; limitations: string[]};
  coachingQuestions: string[];
}

export interface PlayerAIProfile {
  id: string;
  playerId: string;
  scope: PlayerAIProfileScope;
  report: PlayerAIProfileReport;
  evidence: {samples: PlayerAIProfileSample[]; allowedEvidenceRefs: string[]};
  model: string;
  promptVersion: number;
  schemaVersion: number;
  generatedAt: string;
}

type UnknownRecord = Record<string, unknown>;

const own = (value: UnknownRecord, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);

const object = (value: unknown, required: readonly string[], optional: readonly string[] = []): UnknownRecord => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('Invalid player AI profile response');
  const result = value as UnknownRecord;
  const allowed = new Set([...required, ...optional]);
  if (required.some(key => !own(result, key)) || Object.keys(result).some(key => !allowed.has(key))) {
    throw new Error('Invalid player AI profile response');
  }
  return result;
};

const array = (value: unknown, max: number, min = 0): unknown[] => {
  if (!Array.isArray(value) || value.length < min || value.length > max) throw new Error('Invalid player AI profile response');
  return value;
};

const string = (value: unknown, allowEmpty = true): string => {
  if (typeof value !== 'string' || (!allowEmpty && value.trim() === '')) throw new Error('Invalid player AI profile response');
  return value;
};

const boolean = (value: unknown): boolean => {
  if (typeof value !== 'boolean') throw new Error('Invalid player AI profile response');
  return value;
};

const finite = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Invalid player AI profile response');
  return value;
};

const integer = (value: unknown, positive = false): number => {
  const result = finite(value);
  if (!Number.isSafeInteger(result) || (positive ? result <= 0 : result < 0)) throw new Error('Invalid player AI profile response');
  return result;
};

const uuid = (value: unknown): string => requireUUID(value, 'Invalid player AI profile response');
const date = (value: unknown): string => requireRFC3339(value, 'Invalid player AI profile response');

const lens = (value: unknown): WyscoutAnalyticsLens => {
  const result = string(value);
  if (!wyscoutAnalyticsLenses.includes(result as WyscoutAnalyticsLens)) throw new Error('Invalid player AI profile response');
  return result as WyscoutAnalyticsLens;
};

const strings = (value: unknown, max: number, min = 0): string[] => array(value, max, min).map(item => string(item, false));

const sampleDate = (value: unknown): string | null => {
  const result = string(value);
  if (result === '') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(result) || Number.isNaN(Date.parse(`${result}T00:00:00Z`))) throw new Error('Invalid player AI profile response');
  return result;
};

const sample = (value: unknown): PlayerAIProfileSample => {
  const dto = object(value, ['playerId', 'name', 'position', 'appearances', 'starts', 'minutes', 'requestedGames', 'limited', 'dateFrom', 'dateTo']);
  const starts = dto.starts === null ? null : integer(dto.starts);
  const dateFrom = sampleDate(dto.dateFrom);
  const dateTo = sampleDate(dto.dateTo);
  if ((dateFrom === null) !== (dateTo === null) || (dateFrom !== null && dateTo !== null && dateFrom > dateTo)) throw new Error('Invalid player AI profile response');
  return {
    playerId: uuid(dto.playerId), name: string(dto.name, false), position: string(dto.position, false),
    appearances: integer(dto.appearances), starts, minutes: integer(dto.minutes), requestedGames: integer(dto.requestedGames),
    limited: boolean(dto.limited), dateFrom, dateTo,
  };
};

const evidencePlayer = (value: unknown): PlayerAIProfileSample => {
  const dto = object(value, ['sample', 'metrics', 'profiles', 'heatmapZones', 'passOrigins', 'shotLocations', 'trends']);
  array(dto.metrics, 24).forEach(value => {
    const metric = object(value, ['ref', 'key', 'label', 'value'], ['unit']);
    string(metric.ref, false); string(metric.key, false); string(metric.label, false); finite(metric.value);
    if (own(metric, 'unit')) string(metric.unit);
  });
  array(dto.profiles, 16).forEach(value => {
    const profile = object(value, ['ref', 'profile', 'key', 'label', 'value'], ['unit']);
    string(profile.ref, false); string(profile.profile, false); string(profile.key, false); string(profile.label, false); finite(profile.value);
    if (own(profile, 'unit')) string(profile.unit);
  });
  array(dto.heatmapZones, 12).forEach(value => {
    const zone = object(value, ['ref', 'category', 'label', 'count'], ['share']);
    string(zone.ref, false); string(zone.category, false); string(zone.label, false); integer(zone.count);
    if (own(zone, 'share')) finite(zone.share);
  });
  for (const key of ['passOrigins', 'shotLocations'] as const) {
    array(dto[key], 12).forEach(value => {
      const location = object(value, ['ref', 'matchId', 'minute', 'x', 'y'], ['outcome', 'category']);
      string(location.ref, false); integer(location.matchId, true); integer(location.minute); finite(location.x); finite(location.y);
      if (own(location, 'outcome')) string(location.outcome);
      if (own(location, 'category')) string(location.category);
    });
  }
  array(dto.trends, 16).forEach(value => {
    const trend = object(value, ['ref', 'trend', 'matchId', 'value'], ['opponent', 'unit']);
    string(trend.ref, false); string(trend.trend, false); integer(trend.matchId, true); finite(trend.value);
    if (own(trend, 'opponent')) string(trend.opponent);
    if (own(trend, 'unit')) string(trend.unit);
  });
  return sample(dto.sample);
};

const claim = (value: unknown): PlayerAIProfileClaim => {
  const dto = object(value, ['title', 'analysis', 'evidence_refs']);
  return {title: string(dto.title, false), analysis: string(dto.analysis, false), evidenceRefs: strings(dto.evidence_refs, 6, 1)};
};

const report = (value: unknown): PlayerAIProfileReport => {
  const dto = object(value, [
    'archetype', 'evidence_summary', 'strengths', 'development_priorities', 'tactical_fit',
    'peer_comparisons', 'omitted_peers', 'confidence', 'coaching_questions',
  ]);
  const archetype = object(dto.archetype, ['title', 'summary']);
  const confidence = object(dto.confidence, ['level', 'limitations']);
  const level = string(confidence.level);
  if (level !== 'low' && level !== 'medium' && level !== 'high') throw new Error('Invalid player AI profile response');
  return {
    archetype: {title: string(archetype.title, false), summary: string(archetype.summary, false)},
    evidenceSummary: string(dto.evidence_summary, false),
    strengths: array(dto.strengths, 5, 1).map(claim),
    developmentPriorities: array(dto.development_priorities, 5, 1).map(claim),
    tacticalFit: array(dto.tactical_fit, 5, 1).map(value => {
      const fit = object(value, ['role', 'analysis', 'evidence_refs']);
      return {role: string(fit.role, false), analysis: string(fit.analysis, false), evidenceRefs: strings(fit.evidence_refs, 6, 1)};
    }),
    omittedPeers: array(dto.omitted_peers, 3).map(value => {
      const omitted = object(value, ['peer_id', 'name', 'reason']);
      return {peerId: uuid(omitted.peer_id), name: string(omitted.name, false), reason: string(omitted.reason, false)};
    }),
    peerComparisons: array(dto.peer_comparisons, 3).map(value => {
      const comparison = object(value, ['peer_id', 'analysis', 'evidence_refs']);
      return {peerId: uuid(comparison.peer_id), analysis: string(comparison.analysis, false), evidenceRefs: strings(comparison.evidence_refs, 6, 1)};
    }),
    confidence: {level, limitations: strings(confidence.limitations, 6, 1)},
    coachingQuestions: strings(dto.coaching_questions, 6, 1),
  };
};

const nullableSeason = (value: unknown): number | null => {
  const dto = object(value, ['Int64', 'Valid']);
  const valid = boolean(dto.Valid);
  const id = integer(dto.Int64);
  if (valid && id === 0) throw new Error('Invalid player AI profile response');
  if (!valid && id !== 0) throw new Error('Invalid player AI profile response');
  return valid ? id : null;
};

const canonicalScope = (value: unknown): PlayerAIProfileScope => {
  const dto = object(value, ['lens', 'seasonId', 'matchIds', 'peerIds']);
  const seasonId = dto.seasonId === null ? null : integer(dto.seasonId, true);
  const matchIds = Array.from(new Set(array(dto.matchIds, 100).map(id => integer(id, true)))).sort((a, b) => a - b);
  const peerIds = Array.from(new Set(array(dto.peerIds, 3).map(uuid)));
  return {lens: lens(dto.lens), seasonId, matchIds, peerIds};
};

const canonicalRequestScope = (playerId: string, value: unknown): PlayerAIProfileScope => {
  const scope = canonicalScope(value);
  if (scope.peerIds.includes(playerId)) throw new Error('Invalid player AI profile scope');
  return scope;
};

const equalScope = (left: PlayerAIProfileScope, right: PlayerAIProfileScope): boolean =>
  left.lens === right.lens && left.seasonId === right.seasonId
  && left.matchIds.length === right.matchIds.length && left.matchIds.every((id, index) => id === right.matchIds[index])
  && left.peerIds.length === right.peerIds.length && left.peerIds.every(id => right.peerIds.includes(id));

const mapProfile = (
  value: unknown,
  expectedWorkspaceId: string,
  expectedPlayerId: string,
  expectedScope: PlayerAIProfileScope,
): PlayerAIProfile | null => {
  const envelope = object(value, ['data']);
  if (envelope.data === null) return null;
  const dto = object(envelope.data, [
    'id', 'workspace_id', 'player_id', 'created_by', 'lens', 'season_id', 'match_ids', 'peer_ids',
    'context_hash', 'report', 'evidence', 'model', 'prompt_version', 'schema_version', 'generated_at',
  ]);
  const workspaceId = uuid(dto.workspace_id);
  const playerId = uuid(dto.player_id);
  const scope = canonicalScope({lens: dto.lens, seasonId: nullableSeason(dto.season_id), matchIds: dto.match_ids, peerIds: dto.peer_ids});
  if (workspaceId !== expectedWorkspaceId || playerId !== expectedPlayerId || !equalScope(scope, expectedScope)) {
    throw new Error('Invalid player AI profile response');
  }
  if (dto.created_by !== null) uuid(dto.created_by);
  string(dto.context_hash, false);
  const evidence = object(dto.evidence, ['lens', 'main', 'peers', 'allowedEvidenceRefs']);
  if (lens(evidence.lens) !== scope.lens) throw new Error('Invalid player AI profile response');
  const allowedEvidenceRefs = strings(evidence.allowedEvidenceRefs, 368);
  const mainSample = evidencePlayer(evidence.main);
  const peerSamples = array(evidence.peers, 3).map(evidencePlayer);
  const peerSampleIds = peerSamples.map(value => value.playerId);
  const peerIdSet = new Set(peerSampleIds);
  if (mainSample.playerId !== playerId || peerIdSet.size !== peerSampleIds.length
    || peerIdSet.size !== scope.peerIds.length || scope.peerIds.some(id => !peerIdSet.has(id))) {
    throw new Error('Invalid player AI profile response');
  }
  const decodedReport = report(dto.report);
  const comparisonIds = decodedReport.peerComparisons.map(comparison => comparison.peerId);
  if (new Set(comparisonIds).size !== comparisonIds.length || comparisonIds.some(id => !peerIdSet.has(id))) {
    throw new Error('Invalid player AI profile response');
  }
  const omittedIds = decodedReport.omittedPeers.map(item => item.peerId);
  if (new Set(omittedIds).size !== omittedIds.length || omittedIds.some(id => !peerIdSet.has(id))
    || comparisonIds.some(id => omittedIds.includes(id)) || comparisonIds.length + omittedIds.length !== peerIdSet.size) {
    throw new Error('Invalid player AI profile response');
  }
  if (decodedReport.omittedPeers.some(item => peerSamples.find(sample => sample.playerId === item.peerId)?.name !== item.name)) {
    throw new Error('Invalid player AI profile response');
  }
  const allowed = new Set(allowedEvidenceRefs);
  const reportRefs = [
    ...decodedReport.strengths.flatMap(item => item.evidenceRefs),
    ...decodedReport.developmentPriorities.flatMap(item => item.evidenceRefs),
    ...decodedReport.tacticalFit.flatMap(item => item.evidenceRefs),
    ...decodedReport.peerComparisons.flatMap(item => item.evidenceRefs),
  ];
  if (reportRefs.some(ref => !allowed.has(ref))) throw new Error('Invalid player AI profile response');
  return {
    id: uuid(dto.id),
    playerId,
    scope,
    report: decodedReport,
    evidence: {samples: [mainSample, ...peerSamples], allowedEvidenceRefs},
    model: string(dto.model, false),
    promptVersion: integer(dto.prompt_version, true),
    schemaVersion: integer(dto.schema_version, true),
    generatedAt: date(dto.generated_at),
  };
};

const params = (scope: PlayerAIProfileScope): URLSearchParams => {
  const canonical = canonicalScope(scope);
  const result = new URLSearchParams({lens: canonical.lens});
  if (canonical.seasonId !== null) result.set('season_id', String(canonical.seasonId));
  canonical.matchIds.forEach(id => result.append('match_id', String(id)));
  canonical.peerIds.forEach(id => result.append('peer_id', id));
  return result;
};

const rethrow = (error: unknown): never => {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') throw error;
  if (typeof error === 'object' && error !== null) {
    const cancelled = error as UnknownRecord;
    if (cancelled.name === 'CanceledError' || cancelled.code === 'ERR_CANCELED') throw error;
  }
  throw getV2Error(error);
};

export const playerAIProfileKey = (workspaceId: string, playerId: string, scope: PlayerAIProfileScope) => [
  'v2', 'players', workspaceId, 'detail', playerId, 'ai-profile', (() => {
    const canonical = canonicalRequestScope(playerId, scope);
    return {...canonical, peerIds: [...canonical.peerIds].sort()};
  })(),
] as const;

export const playerAIProfileAPI = {
  latest: async (workspaceId: string, playerId: string, scope: PlayerAIProfileScope, signal: AbortSignal): Promise<PlayerAIProfile | null> => {
    try {
      requireUUID(workspaceId, 'Invalid workspace ID');
      requireUUID(playerId, 'Invalid player ID');
      const canonical = canonicalRequestScope(playerId, scope);
      const response = await api.get<unknown>(`/v2/players/${playerId}/ai-profiles/latest`, {
        headers: {'X-Workspace-ID': workspaceId}, params: params(canonical), signal,
      });
      return mapProfile(response.data, workspaceId, playerId, canonical);
    } catch (error) {
      return rethrow(error);
    }
  },
  generate: async (workspaceId: string, playerId: string, scope: PlayerAIProfileScope, signal: AbortSignal): Promise<PlayerAIProfile> => {
    try {
      requireUUID(workspaceId, 'Invalid workspace ID');
      requireUUID(playerId, 'Invalid player ID');
      const canonical = canonicalRequestScope(playerId, scope);
      const response = await api.post<unknown>(`/v2/players/${playerId}/ai-profiles`, {
        lens: canonical.lens,
        season_id: canonical.seasonId,
        match_ids: canonical.matchIds,
        peer_ids: canonical.peerIds,
      }, {headers: {'X-Workspace-ID': workspaceId}, signal});
      const result = mapProfile(response.data, workspaceId, playerId, canonical);
      if (result === null) throw new Error('Invalid player AI profile response');
      return result;
    } catch (error) {
      return rethrow(error);
    }
  },
};
